"use client";
import { styled } from "@mui/material/styles";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";

const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) =>
    prop !== "open" && prop !== "clippedDrawer" && prop !== "drawerWidth",
})<{ open?: boolean; clippedDrawer: boolean; drawerWidth: number | string }>(
  ({ theme, open, clippedDrawer, drawerWidth }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open &&
      !clippedDrawer && {
        [theme.breakpoints.up("sm")]: {
          marginLeft: drawerWidth,
          width: `calc(100% - ${drawerWidth}px)`,
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }),
  }),
);

export interface BaseAppBarProps {
  open?: boolean;
  clippedDrawer: boolean;
  drawerWidth: number | string;
  onClickMenuButton: () => void;
  onClickMobileMenuButton: () => void;
  AppBarProps?: MuiAppBarProps;
}

interface AppBarProps extends BaseAppBarProps {
  IconButtonProps?: {
    normal: Omit<IconButtonProps, "onClick">;
    mobile: Omit<IconButtonProps, "onClick">;
  };
  children?: JSX.Element | JSX.Element[];
}

export function AppBar(props: AppBarProps) {
  return (
    <StyledAppBar
      {...props.AppBarProps}
      position="fixed"
      open={props.open}
      clippedDrawer={props.clippedDrawer}
      drawerWidth={props.drawerWidth}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          onClick={props.onClickMenuButton}
          edge="start"
          {...props.IconButtonProps?.normal}
          sx={{
            mr: 5,
            display: {
              xs: "none",
              sm: props.open && !props.clippedDrawer ? "none" : "inline-flex",
            },
            ...props.IconButtonProps?.normal.sx,
          }}
        >
          <MenuIcon id="ic_menu_button" />
        </IconButton>
        <IconButton
          color="inherit"
          onClick={props.onClickMobileMenuButton}
          edge="start"
          {...props.IconButtonProps?.mobile}
          sx={{
            marginRight: 5,
            display: { xs: "inline-flex", sm: "none" },
            paddingLeft: { xs: "12px", sm: "8px" },
            ...props.IconButtonProps?.mobile.sx,
          }}
        >
          <MenuIcon id="ic_menu_button" />
        </IconButton>
        {props.children}
      </Toolbar>
    </StyledAppBar>
  );
}
